import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import Button, { type ButtonClickHandler } from '@splunk/react-ui/Button';
import P from '@splunk/react-ui/Paragraph';

import { _ } from '@splunk/ui-utils/i18n';

import {
    Modal,
    type returnFocus,
    type ModalRequestCloseHandler,
} from '../Modal';

const FixedWidthModal = styled(Modal)`
    width: 600px;
`;

export type GalleryDeletionModalProps = {
    testId?: string;
    objectName: string;
    objectType: 'icon' | 'image' | 'item';
    sourceElement?: React.MutableRefObject<HTMLElement>;
    handleObjectDeletion: ButtonClickHandler;
    handleDeletionCancel?: (
        e: MouseEvent | React.MouseEvent | KeyboardEvent | TouchEvent
    ) => void;
    returnFocus: returnFocus;
};

export const GalleryDeletionModal = ({
    testId = 'gallery-deletion-modal',
    objectName,
    objectType,
    sourceElement,
    handleObjectDeletion,
    handleDeletionCancel,
    returnFocus,
}: GalleryDeletionModalProps): JSX.Element => {
    // onClose callback (cancel or close - do not delete)
    const handleCancelDeletion = useCallback<React.MouseEventHandler>(
        (event) => {
            if (handleDeletionCancel) {
                handleDeletionCancel(event);
            }

            sourceElement?.current?.focus();
        },
        [sourceElement, handleDeletionCancel]
    );

    // handle close requests (esc, click-away)
    const handleRequestClose = useCallback<ModalRequestCloseHandler>(
        ({ event }) => handleCancelDeletion(event as React.MouseEvent),
        [handleCancelDeletion]
    );

    // Modal body in the format: 'Deleting the <i>{NAME}</> {TYPE} is permanent ... use the {TYPE}'
    const modalBody = useMemo(() => {
        const preamble = _('Deleting the ');

        let postscript = _(
            'is permanent for this dashboard and all other dashboards that use the'
        );
        postscript = ` ${objectType} ${postscript} ${objectType}.`;

        return (
            <P>
                {preamble}
                <i>{objectName}</i>
                {postscript}
            </P>
        );
    }, [objectName, objectType]);

    const capitalizedObjectType = objectType.length
        ? objectType[0].toUpperCase() + objectType.substring(1)
        : objectType;

    return (
        <FixedWidthModal
            data-test={testId}
            onRequestClose={handleRequestClose}
            open
            divider="none"
            returnFocus={returnFocus}
        >
            <Modal.Header title={`${_('Delete')} ${objectName}`} />

            <Modal.Body>{modalBody}</Modal.Body>

            <Modal.Footer>
                <Button
                    data-test={`${testId}-cancel`}
                    appearance="secondary"
                    onClick={handleCancelDeletion}
                    label={_(`Keep ${capitalizedObjectType}`)}
                />
                <Button
                    data-test={`${testId}-confirm`}
                    appearance="destructive"
                    label={_(`Delete ${capitalizedObjectType}`)}
                    onClick={handleObjectDeletion}
                />
            </Modal.Footer>
        </FixedWidthModal>
    );
};
